import type {
  TypeArticleWithoutUnresolvableLinksResponse,
  TypeEventWithoutUnresolvableLinksResponse,
  TypePageBuilderWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import {
  fetchContentJourneys,
  type ContentJourneyData,
} from '~/services/contentJourney'

type JourneySourceEntry =
  | TypeArticleWithoutUnresolvableLinksResponse
  | TypeEventWithoutUnresolvableLinksResponse
  | TypePageBuilderWithoutUnresolvableLinksResponse

export function useContentJourney(
  entry: MaybeRefOrGetter<JourneySourceEntry | null | undefined>,
) {
  const { $client, $visibilityDecision } = useNuxtApp()
  const { locale } = useI18n()

  const currentEntry = computed(() => toValue(entry))
  const entryId = computed(() => currentEntry.value?.sys?.id || '')
  const asyncKey = computed(
    () => `content-journey:${locale.value}:${entryId.value}`,
  )

  const { data, status } = useLazyAsyncData<ContentJourneyData[]>(
    asyncKey,
    async () => {
      if (!entryId.value) return []

      try {
        return await fetchContentJourneys({
          client: $client,
          locale: locale.value,
          currentEntryId: entryId.value,
          isEntryVisible: (journeyEntry) =>
            $visibilityDecision(journeyEntry.fields?.visibility).visible ===
            true,
        })
      } catch (loadError) {
        // Journey navigation is an enhancement and must not break the page.
        if (import.meta.dev) {
          console.warn('[content-journey] failed to load', loadError)
        }
        return []
      }
    },
    {
      server: false,
      default: () => [],
      watch: [entryId, () => locale.value],
    },
  )

  return {
    journeys: data,
    journey: computed(() => data.value?.[0] || null),
    isLoading: computed(
      () => status.value === 'pending' && Boolean(entryId.value),
    ),
  }
}
